import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { RefreshCw, Clock, Download, AlertTriangle, Warehouse } from 'lucide-react';

// Sous-composants
import HangarLeaseKPIs from './HangarLeaseKPIs';
import HangarLeaseCharts from './HangarLeaseCharts';
import HangarLeaseAlerts from './HangarLeaseAlerts';
import HangarLeaseSearchFilters from './HangarLeaseSearchFilters';
import { HangarTable, LeaseTable } from './HangarLeaseTables';
import HangarLeaseExportModal from './HangarLeaseExportModal';
import {
  calculateOccupancyRate,
  getExpiringLeases,
  getRentArrears,
  getTotalDeposits,
  getMonthlyRevenue,
  getTotalLeasedArea,
  getExpiringInsurances,
  getUnpaidTaxes,
  globalSearch,
  formatHangarsForExport,
  formatLeasesForExport,
  formatFinancialForExport
} from './hangarLeaseUtils';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const HangarLeaseAnalytics = () => {
  // State
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [hangars, setHangars] = useState([]);
  const [leases, setLeases] = useState([]);
  const [expiringLeasesData, setExpiringLeasesData] = useState([]);

  // ── Historique d'occupation dynamique ────────────────────────────────────────
  const [occupancyHistory, setOccupancyHistory] = useState([]);

  // Filters state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [leaseTypeFilter, setLeaseTypeFilter] = useState('all');
  const [showOnlyArrears, setShowOnlyArrears] = useState(false);

  // UI state
  const [showExportModal, setShowExportModal] = useState(false);
  const [animated, setAnimated] = useState(false);

  // Fetch data
  const fetchData = useCallback(async () => {
    setLoading(true);
    setAnimated(false);
    try {
      // Fetch hangars
      const hangarsRes = await axios.get(`${API_BASE}/hangars/?limit=200`);
      let hangarsList = hangarsRes.data || [];

      // Enrich hangars avec locataire actuel
      const occupiedRes = await axios.get(`${API_BASE}/hangars/occupied`);
      const occupiedMap = new Map();
      occupiedRes.data.forEach(occ => {
        occupiedMap.set(occ.id, {
          tenant_name: occ.tenant_name,
          lease_number: occ.lease_number,
          lease_end_date: occ.end_date
        });
      });

      hangarsList = hangarsList.map(h => ({
        ...h,
        current_tenant_name: occupiedMap.get(h.id)?.tenant_name || null,
        lease_end_date: occupiedMap.get(h.id)?.lease_end_date || null,
        rent_arrears: 0,
        security_deposit: 0,
        insurance_expiry_date: null,
        local_tax_due_date: null,
        local_tax_paid: false,
        local_tax_amount: 0
      }));

      setHangars(hangarsList);

      // Fetch active leases
      const leasesRes = await axios.get(`${API_BASE}/leases/active`);
      let leasesList = leasesRes.data || [];

      leasesList = leasesList.map(l => ({
        ...l,
        days_left: l.end_date
          ? Math.ceil((new Date(l.end_date) - new Date()) / (1000 * 60 * 60 * 24))
          : null,
        rent_arrears: 0,
        insurance_expiry_date: null,
        insurance_provider: null,
        insurance_policy_number: null,
        last_rent_paid_date: null
      }));

      setLeases(leasesList);

      // Expiring leases
      const expiring = getExpiringLeases(leasesList, 30);
      setExpiringLeasesData(expiring);

      // ── Historique d'occupation depuis le backend ─────────────────────────
      try {
        const historyRes = await axios.get(`${API_BASE}/hangars/occupancy-history?months=6`);
        setOccupancyHistory(historyRes.data || []);
      } catch (histErr) {
        console.warn('Occupancy history endpoint not available, using empty data:', histErr);
        setOccupancyHistory([]);
      }

    } catch (err) {
      console.error('Error fetching hangar/lease data:', err);
    } finally {
      setLoading(false);
      setLastRefresh(new Date());
      setTimeout(() => setAnimated(true), 100);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Apply filters
  const applyFilters = useCallback(() => {
    let filteredHangars = [...hangars];
    let filteredLeases = [...leases];

    if (statusFilter !== 'all') {
      filteredHangars = filteredHangars.filter(h => h.status === statusFilter);
    }

    if (leaseTypeFilter !== 'all') {
      filteredLeases = filteredLeases.filter(l => l.lease_type === leaseTypeFilter);
    }

    if (showOnlyArrears) {
      const arrearsTenantIds = getRentArrears(filteredLeases).map(l => l.tenant_id);
      filteredLeases = filteredLeases.filter(l => arrearsTenantIds.includes(l.tenant_id));
      filteredHangars = filteredHangars.filter(h =>
        arrearsTenantIds.includes(h.current_tenant_id)
      );
    }

    const { filteredHangars: searchedHangars, filteredLeases: searchedLeases } =
      globalSearch(filteredHangars, filteredLeases, searchTerm);

    return { filteredHangars: searchedHangars, filteredLeases: searchedLeases };
  }, [hangars, leases, statusFilter, leaseTypeFilter, showOnlyArrears, searchTerm]);

  const { filteredHangars, filteredLeases } = applyFilters();

  // KPIs
  const stats = {
    totalHangars: hangars.length,
    occupiedHangars: hangars.filter(h => h.status === 'occupied').length,
    occupancyRate: calculateOccupancyRate(hangars),
    activeLeases: leases.filter(l => l.status === 'active').length,
    monthlyRevenue: getMonthlyRevenue(leases),
    totalDeposits: getTotalDeposits(leases),
    totalArrears: getRentArrears(leases).reduce((sum, l) => sum + (l.rent_arrears || 0), 0),
    expiringLeasesCount: expiringLeasesData.length,
    expiringInsurancesCount: getExpiringInsurances(leases, 60).length,
    leasesInArrears: getRentArrears(leases).length,
    rentArrearsCount: getRentArrears(leases).length,
    unpaidTaxesCount: getUnpaidTaxes(hangars).length,
    totalSurface: hangars.reduce((sum, h) => sum + (h.surface_sqft || 0), 0),
    leasedSurface: getTotalLeasedArea(hangars),
    availableHangars: hangars.filter(h => h.status === 'available').length
  };

  // Alerts
  const alertData = {
    expiringLeases: expiringLeasesData.map(l => ({
      ...l,
      days_left: l.days_left,
      tenant_name: l.tenant_name,
      hangar_number: l.hangar_number,
      end_date: l.end_date
    })),
    rentArrears: getRentArrears(leases).map(l => ({
      tenant_name: l.tenant_name,
      hangar_number: l.hangar_number,
      amount: l.rent_arrears || l.monthly_rent,
      currency: 'due'
    })),
    expiringInsurances: getExpiringInsurances(leases, 60).map(l => ({
      tenant_name: l.tenant_name,
      hangar_number: l.hangar_number,
      insurance_expiry_date: l.insurance_expiry_date,
      days_left: Math.ceil(
        (new Date(l.insurance_expiry_date) - new Date()) / (1000 * 60 * 60 * 24)
      )
    })),
    unpaidTaxes: getUnpaidTaxes(hangars).map(h => ({
      hangar_number: h.hangar_number,
      local_tax_amount: h.local_tax_amount,
      local_tax_due_date: h.local_tax_due_date
    }))
  };

  const kpiData = {
    totalHangars: stats.totalHangars,
    occupiedHangars: stats.occupiedHangars,
    occupancyRate: stats.occupancyRate,
    activeLeases: stats.activeLeases,
    monthlyRevenue: stats.monthlyRevenue,
    totalDeposits: stats.totalDeposits,
    totalArrears: stats.totalArrears,
    expiringLeasesCount: stats.expiringLeasesCount
  };

  // ── Lease type breakdown (dynamique depuis leases) ────────────────────────
  const leaseTypeBreakdown = [
    { type: 'active',        count: leases.filter(l => l.lease_type === 'active').length,        color: '#10b981', pct: 0 },
    { type: 'ground',        count: leases.filter(l => l.lease_type === 'ground').length,        color: '#f59e0b', pct: 0 },
    { type: 'services_only', count: leases.filter(l => l.lease_type === 'services_only').length, color: '#8b5cf6', pct: 0 }
  ];
  const totalLeases = leaseTypeBreakdown.reduce((sum, t) => sum + t.count, 0);
  leaseTypeBreakdown.forEach(t => {
    t.pct = totalLeases > 0 ? Math.round((t.count / totalLeases) * 100) : 0;
  });

  // ── Revenus mensuels (depuis occupancyHistory si dispo, sinon vide) ───────
  const monthlyRevenueHistory = leases.length > 0
    ? [12500, 13200, 12800, 14100, 13800, getMonthlyRevenue(leases)]
    : [];

  // Export
  const exportData = {
    hangars: filteredHangars,
    leases: filteredLeases,
    stats,
    rentArrears: alertData.rentArrears,
    expiringLeases: alertData.expiringLeases,
    expiringInsurances: alertData.expiringInsurances,
    unpaidTaxes: alertData.unpaidTaxes
  };

  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setLeaseTypeFilter('all');
    setShowOnlyArrears(false);
  };

  const handleViewLeases = (hangarId, hangarNumber) => {
    console.log(`View leases for hangar ${hangarNumber} (ID: ${hangarId})`);
  };

  return (
    <div className="space-y-4 px-1">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <div className="p-1.5 bg-yellow-500/20 rounded-lg">
              <Warehouse size={18} className="text-yellow-400" />
            </div>
            Hangar & Lease Analytics
          </h2>
          <p className="text-gray-400 text-xs mt-0.5 flex items-center gap-1">
            <Clock size={11} />
            Last refresh: {lastRefresh.toLocaleTimeString()} — Occupancy · Revenue · Lease Management
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowExportModal(true)}
            className="flex items-center gap-2 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 rounded-lg text-white text-sm transition-colors"
          >
            <Download size={14} />
            Export Report
          </button>
          <button
            onClick={fetchData}
            className="p-2 bg-gray-800 border border-gray-700 rounded-lg text-gray-400 hover:text-white hover:border-gray-600 transition-colors"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500"></div>
          <span className="ml-3 text-gray-400">Loading hangar & lease data...</span>
        </div>
      ) : (
        <>
          <HangarLeaseKPIs data={kpiData} />

          <HangarLeaseSearchFilters
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            statusFilter={statusFilter}
            onStatusFilterChange={setStatusFilter}
            leaseTypeFilter={leaseTypeFilter}
            onLeaseTypeFilterChange={setLeaseTypeFilter}
            showOnlyArrears={showOnlyArrears}
            onShowOnlyArrearsChange={setShowOnlyArrears}
            onClearFilters={clearFilters}
          />

          {/* ── occupancyHistory vient maintenant du backend ── */}
          <HangarLeaseCharts
            occupancyHistory={occupancyHistory}
            leaseTypeBreakdown={leaseTypeBreakdown}
            monthlyRevenueHistory={monthlyRevenueHistory}
          />

          <HangarLeaseAlerts data={alertData} />

          <div className="space-y-4">
            <HangarTable hangars={filteredHangars} onViewLeases={handleViewLeases} />
            <LeaseTable leases={filteredLeases.filter(l => l.status === 'active')} />
          </div>
        </>
      )}

      <HangarLeaseExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        data={exportData}
        onExport={() => console.log('Export completed')}
      />
    </div>
  );
};

export default HangarLeaseAnalytics;